import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { resolveUserRole, type UserRole } from './types/auth';
import { isMockAuthEnabled, loadMockSession } from './utils/mockAuth';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: UserRole | null;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  const useMockAuth = isMockAuthEnabled();

  const refreshSession = useCallback(async () => {
    try {
      if (useMockAuth) {
        const mockSession = loadMockSession();
        setSession(mockSession);
        setUser(mockSession?.user ?? null);
        return;
      }
      
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('[AuthContext] Failed to refresh session', error);
        setSession(null);
        setUser(null);
        return;
      }

      setSession(data.session ?? null);
      setUser(data.session?.user ?? null);
    } catch (err) {
      console.error('[AuthContext] Unexpected error refreshing session', err);
      setSession(null);
      setUser(null);
    }
  }, [useMockAuth]);

  useEffect(() => {
    let isMounted = true;

    const loadInitialSession = async () => {
      try {
        if (useMockAuth) {
          const mockSession = loadMockSession();
          if (isMounted) {
            setSession(mockSession);
            setUser(mockSession?.user ?? null);
            setLoading(false);
          }
          return;
        }
        
        const { data, error } = await supabase.auth.getSession();
        if (!isMounted) {
          return;
        }

        if (error) {
          console.error('[AuthContext] Failed to fetch session', error);
          setSession(null);
          setUser(null);
        } else {
          setSession(data.session ?? null);
          setUser(data.session?.user ?? null);
        }
      } catch (err) {
        if (isMounted) {
          console.error('[AuthContext] Unexpected error fetching session', err);
          setSession(null);
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInitialSession();

    // Skip auth listener for mock mode
    if (useMockAuth) {
      return () => {
        isMounted = false;
      };
    }

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, authSession) => {
      if (!isMounted) {
        return;
      }

      setSession(authSession ?? null);
      setUser(authSession?.user ?? null);
      setLoading(false);
    });

    return () => {
      isMounted = false;
      authListener?.subscription.unsubscribe();
    };
  }, [useMockAuth]);

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      role: resolveUserRole(user),
      refreshSession
    }),
    [user, session, loading, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
